import { useEffect, useMemo, useState } from 'react'
import { Icon } from '@iconify/react'
import type { CampaignSocialLinks } from '@/lib/campaign-social-links'
import { PublicCampaignThemeStyle } from '@/features/publicCampaignTemplates/theme'
import { LandingDefaultFooter } from '@/components/Landing/LandingDefaultFooter'
import { LandingPageInlineWidgetCheckout } from '@/components/Landing/LandingPageInlineWidgetCheckout'
import { LandingSalesClosedNotice } from '@/components/Landing/LandingSalesClosedNotice'
import { LandingSalesClosedModal } from '@/components/Landing/LandingSalesClosedModal'
import { LandingPremiatedNumbersExpandable } from '@/pages/LandingPage/LandingPremiatedNumbersExpandable'
import type { LandingCampaign, LandingInstitution, LandingPremiatedNumberItem } from '@/types/landing'
import {
  normalizeTemplate3Advantages,
  normalizeTemplate3Faq,
  sanitizeTemplate3PublicImageSrc,
} from '@/lib/template3-campaign'
import { SafeLandingHtml } from '@/components/SafeLandingHtml'
import { htmlHasVisibleText, stripHtmlToPlainText } from '@/lib/landing-html'
import { safePublicHttpHref } from '@/lib/public-href'
import { TEMPLATE3_HOW_IT_WORKS_STEPS } from './template3HowItWorks'
import { calculateMinDonationAmount } from './template3MinDonation'
import styles from './PublicCampaignTemplate3.module.css'

type PublicCampaignTemplate3LandingProps = {
  campaign: LandingCampaign
  institution: LandingInstitution
  institutionName: string
  institutionBrandUrl: string | null
  institutionAvatarUrl: string | null
  title: string
  highlightedDescription: string | null
  pricePerNumber: number
  donationOnly: boolean
  salesOpen: boolean
  isEmbedMode: boolean
  contactWhatsappHref: string
  landingSocialLinks: CampaignSocialLinks
  premiatedNumberItems: LandingPremiatedNumberItem[]
  showPremiatedNumbersEntry: boolean
  hasRegulationContent: boolean
  regulationContentType: 'document' | 'text'
  regulationText: string
  isFavorite: boolean
  salesClosedModalOpen: boolean
  regulationOpen: boolean
  premiatedListOpen: boolean
  onCloseSalesClosedModal: () => void
  onAccountAction: () => void
  onFavoriteToggle: () => void
  onRegulationOpenChange: (open: boolean) => void
  onPremiatedListOpenChange: (open: boolean) => void
}

function backgroundImageStyle(url: string | null | undefined): { backgroundImage: string } | undefined {
  if (!url) return undefined
  const safe = url.replace(/\\/g, '').replace(/["')]/g, '')
  if (safe.trim() === '') return undefined
  return { backgroundImage: `url("${safe}")` }
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

function formatDrawDate(value: string | null | undefined): string {
  if (!value) return 'A definir'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'A definir'
  return new Intl.DateTimeFormat('pt-BR').format(date)
}

type Template3DocumentKind = 'regulation' | 'certificate'

type Template3DocumentModalContent = {
  title: string
  description: string
  iframeTitle: string
  pdfUrl: string | null
  showText: boolean
  text: string
}

function buildTemplate3DocumentModalContent(params: {
  kind: Template3DocumentKind
  regulationContentType: 'document' | 'text'
  regulationText: string
  regulationPdfUrl: string | null | undefined
  certificatePdfUrl: string | null
}): Template3DocumentModalContent {
  if (params.kind === 'certificate') {
    return {
      title: 'Certificado',
      description: 'Confira o certificado de autorização da campanha.',
      iframeTitle: 'Certificado da campanha',
      pdfUrl: safePublicHttpHref(params.certificatePdfUrl),
      showText: false,
      text: '',
    }
  }

  return {
    title: 'Regulamento',
    description: 'Leia as regras e condições da campanha.',
    iframeTitle: 'Regulamento da campanha',
    pdfUrl: safePublicHttpHref(params.regulationPdfUrl),
    showText: params.regulationContentType === 'text',
    text: params.regulationText,
  }
}

type Template3DocumentActionButtonsProps = {
  hasRegulationContent: boolean
  hasCertificate: boolean
  buttonClassName: string
  onRegulationClick: () => void
  onCertificateClick: () => void
  fallbackLabel?: string
  onFallbackClick?: () => void
}

function Template3DocumentActionButtons({
  hasRegulationContent,
  hasCertificate,
  buttonClassName,
  onRegulationClick,
  onCertificateClick,
  fallbackLabel,
  onFallbackClick,
}: Template3DocumentActionButtonsProps) {
  if (!hasRegulationContent && !hasCertificate) {
    if (!fallbackLabel || !onFallbackClick) {
      return null
    }

    return (
      <button type="button" className={buttonClassName} onClick={onFallbackClick}>
        {fallbackLabel}
      </button>
    )
  }

  return (
    <>
      {hasRegulationContent && (
        <button type="button" className={buttonClassName} onClick={onRegulationClick}>
          Regulamento
        </button>
      )}
      {hasCertificate && (
        <button type="button" className={buttonClassName} onClick={onCertificateClick}>
          Certificado
        </button>
      )}
    </>
  )
}

function useCountdown(endsAt: string | null | undefined) {
  const [parts, setParts] = useState({ days: '0', hours: '00', minutes: '00', seconds: '00' })

  useEffect(() => {
    if (!endsAt) return

    const target = new Date(endsAt)
    if (Number.isNaN(target.getTime())) return

    const tick = () => {
      const diff = target.getTime() - Date.now()
      if (diff <= 0) {
        setParts({ days: '0', hours: '00', minutes: '00', seconds: '00' })
        return
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)
      setParts({
        days: String(days),
        hours: String(hours).padStart(2, '0'),
        minutes: String(minutes).padStart(2, '0'),
        seconds: String(seconds).padStart(2, '0'),
      })
    }

    tick()
    const interval = window.setInterval(tick, 1000)
    return () => window.clearInterval(interval)
  }, [endsAt])

  return parts
}

export function PublicCampaignTemplate3Landing({
  campaign,
  institution,
  institutionName,
  institutionBrandUrl,
  institutionAvatarUrl,
  title,
  highlightedDescription,
  pricePerNumber,
  donationOnly,
  salesOpen,
  isEmbedMode,
  contactWhatsappHref,
  landingSocialLinks,
  premiatedNumberItems,
  showPremiatedNumbersEntry,
  hasRegulationContent,
  regulationContentType,
  regulationText,
  isFavorite,
  salesClosedModalOpen,
  regulationOpen,
  premiatedListOpen,
  onCloseSalesClosedModal,
  onAccountAction,
  onFavoriteToggle,
  onRegulationOpenChange,
  onPremiatedListOpenChange,
}: PublicCampaignTemplate3LandingProps) {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null)
  const [certificateOpen, setCertificateOpen] = useState(false)
  const countdown = useCountdown(campaign.ends_at)
  const ctaHtml = htmlHasVisibleText(campaign.cta_text) ? campaign.cta_text : 'ADQUIRA SEU NÚMERO'
  const ctaPlainText = stripHtmlToPlainText(ctaHtml)
  const advantages = normalizeTemplate3Advantages(campaign.template3_advantages)
  const faq = normalizeTemplate3Faq(campaign.template3_faq)
  const certificateUrl = campaign.certificate_pdf_url?.trim() || null
  const whatsappButtonHref = safePublicHttpHref(campaign.whatsapp_button_url)
  const heroBackground = campaign.template3_hero_background_url?.trim() || ''
  const aboutImage = campaign.template3_about_image_url?.trim() || ''
  const floatingCtaImageSrc = sanitizeTemplate3PublicImageSrc(campaign.template3_floating_cta_image_url)
  const prizeImage = campaign.main_image_url?.trim() || ''
  const minDonationAmount = calculateMinDonationAmount(
    pricePerNumber,
    campaign.min_quantity_per_purchase,
  )
  const aboutTitle = htmlHasVisibleText(campaign.template3_about_title)
    ? campaign.template3_about_title
    : 'Sobre o Sorteio'
  const aboutText = campaign.template3_about_text?.trim() || highlightedDescription || ''

  const scrollToCheckout = () => {
    document.getElementById('template3-checkout')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const openRegulationModal = () => {
    setCertificateOpen(false)
    onRegulationOpenChange(true)
  }

  const openCertificateModal = () => {
    onRegulationOpenChange(false)
    setCertificateOpen(true)
  }

  const closeDocumentModal = () => {
    setCertificateOpen(false)
    onRegulationOpenChange(false)
  }

  const documentModalKind: Template3DocumentKind | null = regulationOpen
    ? 'regulation'
    : certificateOpen
      ? 'certificate'
      : null

  const documentModal = documentModalKind
    ? buildTemplate3DocumentModalContent({
        kind: documentModalKind,
        regulationContentType,
        regulationText,
        regulationPdfUrl: campaign.regulation_pdf_url,
        certificatePdfUrl: certificateUrl,
      })
    : null

  const countdownUnits = useMemo(
    () => [
      { key: 'days', value: countdown.days, label: 'DIAS' },
      { key: 'hours', value: countdown.hours, label: 'HORAS' },
      { key: 'minutes', value: countdown.minutes, label: 'MINUTOS' },
      { key: 'seconds', value: countdown.seconds, label: 'SEGUNDOS' },
    ],
    [countdown]
  )

  return (
    <div className={isEmbedMode ? `${styles.page} ${styles.pageEmbed}` : styles.page}>
      <PublicCampaignThemeStyle campaign={campaign} />

      <header
        className={styles.hero}
        style={backgroundImageStyle(heroBackground)}
      >
        <div className={styles.heroOverlay} />
        <div className={styles.heroInner}>
          <div className={styles.heroCopy}>
            <div className={styles.heroLeft}>
              <div className={styles.heroTopRow}>
                {institutionBrandUrl ? (
                  <div className={styles.logo} style={backgroundImageStyle(institutionBrandUrl)} />
                ) : (
                  <div className={styles.logoFallback}>{institutionName.slice(0, 1)}</div>
                )}
                <button type="button" className={`${styles.accountButton} ${styles.accountButtonMobile}`} onClick={onAccountAction}>
                  Minha Conta
                </button>
              </div>
              <div className={styles.heroIntro}>
                {htmlHasVisibleText(campaign.template3_eyebrow) && (
                  <SafeLandingHtml
                    html={campaign.template3_eyebrow}
                    as="p"
                    variant="inline"
                    className={styles.eyebrow}
                  />
                )}
                <SafeLandingHtml html={title} as="h1" variant="inline" className={styles.title} />
                {highlightedDescription && (
                  <SafeLandingHtml
                    html={highlightedDescription}
                    as="div"
                    variant="block"
                    className={styles.text}
                  />
                )}
              </div>
            </div>

            <div className={styles.heroPurchase}>
              {htmlHasVisibleText(campaign.prize_description) && (
                <SafeLandingHtml
                  html={campaign.prize_description}
                  as="div"
                  variant="block"
                  className={styles.prizeText}
                />
              )}
              <button type="button" className={`${styles.ctaButton} ${styles.ctaButtonWide}`} onClick={scrollToCheckout}>
                <SafeLandingHtml html={ctaHtml} as="span" variant="inline" />
              </button>
              <button type="button" className={`${styles.ctaButton} ${styles.heroFavorite}`} onClick={onFavoriteToggle}>
                <Icon icon={isFavorite ? 'mdi:heart' : 'mdi:heart-outline'} />
                {isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
              </button>
            </div>
          </div>

          <div className={styles.heroCenter}>
            {prizeImage ? (
              <img className={styles.heroImage} src={prizeImage} alt={stripHtmlToPlainText(title) || 'Prêmio'} />
            ) : null}
          </div>

          <button type="button" className={`${styles.accountButton} ${styles.accountButtonDesktop}`} onClick={onAccountAction}>
            Minha Conta
          </button>
        </div>
      </header>

      <section className={styles.stats}>
        <div className={styles.statsInner}>
          <div className={styles.statsCards}>
            <div className={styles.stat}>
              <Icon icon="mdi:ticket-confirmation-outline" className={styles.statIcon} />
              <p className={styles.statLabel}>Compra mínima</p>
              <p className={styles.statValue}>{formatCurrency(minDonationAmount)}</p>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.stat}>
              <Icon icon="mdi:calendar-outline" className={styles.statIcon} />
              <p className={styles.statLabel}>Data do sorteio</p>
              <p className={styles.statValue}>{formatDrawDate(campaign.ends_at)}</p>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.stat}>
              <Icon icon="mdi:gift-outline" className={styles.statIcon} />
              <p className={styles.statLabel}>Prêmio</p>
              <p className={styles.statValue}>
                {htmlHasVisibleText(campaign.prize_description) ? (
                  <SafeLandingHtml html={campaign.prize_description} as="span" variant="inline" />
                ) : (
                  'Prêmio a definir'
                )}
              </p>
            </div>
          </div>
          <div className={styles.moreInfo}>
            <div>
              <p className={styles.moreInfoTitle}>MAIS INFORMAÇÕES</p>
              <div className={styles.moreInfoActions}>
                <Template3DocumentActionButtons
                  hasRegulationContent={hasRegulationContent}
                  hasCertificate={Boolean(certificateUrl)}
                  buttonClassName={styles.pillButton}
                  onRegulationClick={openRegulationModal}
                  onCertificateClick={openCertificateModal}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.checkoutSection} aria-label="Comprar números da sorte">
        <div className={styles.checkoutSectionInner} id="template3-checkout">
          {salesOpen ? (
            <LandingPageInlineWidgetCheckout campaign={campaign} className={styles.checkoutCard} />
          ) : (
            <LandingSalesClosedNotice campaign={campaign} visualVariant="light" />
          )}
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.about}>
          <div className={styles.aboutCopy}>
            <SafeLandingHtml html={aboutTitle} as="h2" variant="inline" className={styles.aboutTitle} />
            {aboutImage && <div className={styles.aboutImage} style={backgroundImageStyle(aboutImage)} />}
            {htmlHasVisibleText(aboutText) && (
              <SafeLandingHtml html={aboutText} as="div" variant="block" className={styles.aboutText} />
            )}
            <button type="button" className={`${styles.ctaButton} ${styles.ctaButtonWide}`} onClick={scrollToCheckout}>
              QUERO AJUDAR
            </button>
            {htmlHasVisibleText(campaign.public_notice_text) && (
              <SafeLandingHtml html={campaign.public_notice_text} as="div" variant="block" className={styles.notice} />
            )}
            {showPremiatedNumbersEntry && (
              <LandingPremiatedNumbersExpandable
                items={premiatedNumberItems}
                donationOnly={donationOnly}
                visualVariant="light"
                expanded={premiatedListOpen}
                onExpandedChange={onPremiatedListOpenChange}
              />
            )}
          </div>
          {aboutImage && <div className={styles.aboutImageDesktop} style={backgroundImageStyle(aboutImage)} />}
        </div>
      </section>

      <section className={styles.how}>
        <div className={styles.howInner}>
          <h2 className={styles.sectionHeading}>COMO FUNCIONA O SORTEIO?</h2>
          <ol className={styles.howGrid}>
            {TEMPLATE3_HOW_IT_WORKS_STEPS.map((step) => (
              <li className={styles.howCard} key={step.number}>
                <span className={styles.howNumber} aria-hidden="true">
                  {step.number}
                </span>
                <h3 className={styles.howTitle}>{step.title}</h3>
              </li>
            ))}
          </ol>
          <button type="button" className={styles.ctaButton} onClick={scrollToCheckout}>
            <SafeLandingHtml html={ctaHtml} as="span" variant="inline" />
          </button>
        </div>
      </section>

      <section className={styles.countdown}>
        <div className={styles.countdownInner}>
          <div>
            <p className={styles.countdownTitle}>PARTICIPE ANTES QUE ACABE!</p>
            <button type="button" className={`${styles.ctaButtonDark} ${styles.countdownCtaDesktop}`} onClick={scrollToCheckout}>
              <SafeLandingHtml html={ctaHtml} as="span" variant="inline" />
            </button>
          </div>
          <div>
            <div className={styles.countdownUnits}>
              {countdownUnits.map((unit) => (
                <div className={styles.countdownUnit} key={unit.key}>
                  <div className={styles.countdownBox}>{unit.value}</div>
                  <span className={styles.countdownLabel}>{unit.label}</span>
                </div>
              ))}
            </div>
            <div className={styles.countdownCtaMobile}>
              <button type="button" className={styles.ctaButtonDark} onClick={scrollToCheckout}>
                <SafeLandingHtml html={ctaHtml} as="span" variant="inline" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.why}>
        <h2 className={styles.whyTitle}>POR QUE PARTICIPAR?</h2>
        {advantages.map((advantage, index) => {
          const imageFirst = index === 1
          const image = advantage.image_url ? (
            <div className={styles.whyImage} style={backgroundImageStyle(advantage.image_url)} />
          ) : null

          return (
            <article className={styles.whyCard} key={`advantage-${index}`}>
              {image && <div className={styles.whyImageMobile}>{image}</div>}
              {imageFirst && image && <div className={styles.whyImageDesktop}>{image}</div>}
              <div className={styles.whyCopy}>
                <SafeLandingHtml html={advantage.title} as="h3" variant="inline" className={styles.whyCardTitle} />
                <SafeLandingHtml html={advantage.description} as="div" variant="block" className={styles.whyCardText} />
                <button type="button" className={styles.ctaButton} onClick={scrollToCheckout}>
                  <SafeLandingHtml html={ctaHtml} as="span" variant="inline" />
                </button>
              </div>
              {!imageFirst && image && <div className={styles.whyImageDesktop}>{image}</div>}
            </article>
          )
        })}
      </section>

      <section className={styles.regulationBand}>
        <h2 className={styles.regulationTitle}>Regulamento</h2>
        <p className={styles.regulationText}>Leia atentamente o regulamento antes de participar.</p>
        <div className={styles.regulationActions}>
          <Template3DocumentActionButtons
            hasRegulationContent={hasRegulationContent}
            hasCertificate={Boolean(certificateUrl)}
            buttonClassName={styles.ctaButton}
            onRegulationClick={openRegulationModal}
            onCertificateClick={openCertificateModal}
            fallbackLabel={ctaPlainText}
            onFallbackClick={scrollToCheckout}
          />
        </div>
      </section>

      <section className={styles.faqBand}>
        <div className={styles.faqInner}>
          <h2 className={styles.faqTitle}>DÚVIDAS FREQUENTES</h2>
          {faq.map((item, index) => (
            <div className={styles.faqItem} key={`faq-${index}`}>
              <button
                type="button"
                className={styles.faqButton}
                aria-expanded={openFaqIndex === index}
                onClick={() => setOpenFaqIndex((current) => (current === index ? null : index))}
              >
                <SafeLandingHtml html={item.question} as="span" variant="inline" />
                <span aria-hidden>{openFaqIndex === index ? '▲' : '▼'}</span>
              </button>
              {openFaqIndex === index && (
                <SafeLandingHtml html={item.answer} as="div" variant="block" className={styles.faqAnswer} />
              )}
            </div>
          ))}
        </div>
      </section>

      <LandingDefaultFooter
        campaignTitle={stripHtmlToPlainText(title)}
        institution={institution}
        institutionName={institutionName}
        institutionAvatarUrl={institutionAvatarUrl}
        socialLinks={landingSocialLinks}
        contactWhatsappHref={contactWhatsappHref}
        onAccountAction={onAccountAction}
      />

      {!isEmbedMode && floatingCtaImageSrc && (
        <button
          type="button"
          className={styles.floatingCta}
          onClick={scrollToCheckout}
          aria-label="Compre aqui"
        >
          <img
            className={styles.floatingCtaImage}
            src={floatingCtaImageSrc}
            alt=""
          />
        </button>
      )}

      {!isEmbedMode && whatsappButtonHref && (
        <a
          href={whatsappButtonHref}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.whatsappButton}
          aria-label="Falar no WhatsApp"
        >
          <Icon icon="mdi:whatsapp" />
        </a>
      )}

      {documentModal && (
        <div
          className={styles.modalOverlay}
          role="presentation"
          onClick={closeDocumentModal}
        >
          <div
            className={styles.modalCard}
            role="dialog"
            aria-modal="true"
            aria-labelledby="template3-document-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <div>
                <h2 id="template3-document-title">{documentModal.title}</h2>
                <p>{documentModal.description}</p>
              </div>
              <button type="button" className={styles.modalClose} onClick={closeDocumentModal}>
                <Icon icon="mdi:close" />
              </button>
            </div>
            {documentModal.showText ? (
              <SafeLandingHtml html={documentModal.text} as="div" variant="block" className={styles.modalText} />
            ) : (
              <>
                <iframe title={documentModal.iframeTitle} src={documentModal.pdfUrl || undefined} className={styles.modalFrame} />
                <div className={styles.modalActions}>
                  <a href={documentModal.pdfUrl || '#'} target="_blank" rel="noopener noreferrer" className={styles.ctaButton}>
                    Abrir em nova aba
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <LandingSalesClosedModal open={salesClosedModalOpen} onClose={onCloseSalesClosedModal} />
    </div>
  )
}
